// Ch94 · PKT-0792 Phase 4 · choosing WHEN.
//
// The packet: a datetime picker "defaulting to the next open slot". This is
// that default, and the rules it has to respect are not obvious:
//
//  · a slot must be in the FUTURE. Defaulting to a time that has passed makes
//    the picker's first suggestion invalid, and the operator finds out only on
//    submit.
//  · a day that already holds a pending slot is NOT open. One post a day is the
//    shape the whole calendar draws; offering a second silently doubles up.
//  · the hour is Grant's posting hour, not "now + 5 minutes". A reel scheduled
//    for 02:41 because that happened to be when he clicked is not a schedule.
//
// 🔴 IT RETURNS A PROPOSAL, NEVER A COMMITMENT. Nothing here writes anything.

import type { ScheduledPost } from "./postingCalendar";

/** The hour reels go out, local time. A named constant so it is arguable. */
export const POSTING_HOUR = 9;

/** How far ahead we are willing to look before giving up. */
const SEARCH_DAYS = 60;

const atPostingHour = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), POSTING_HOUR, 0, 0, 0);

const dayStart = (ms: number): number => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/**
 * The next day with no pending slot on it, at the posting hour.
 *
 * Returns `null` rather than a far-future guess when everything in range is
 * taken — the picker then says so instead of proposing a date nobody chose.
 */
export function nextOpenSlot(
  scheduled: ScheduledPost[],
  now: Date = new Date()
): Date | null {
  const taken = new Set<number>();
  scheduled.forEach((slot) => {
    const parsed = Date.parse(slot.scheduledFor);
    if (Number.isFinite(parsed)) taken.add(dayStart(parsed));
  });

  for (let offset = 0; offset < SEARCH_DAYS; offset += 1) {
    const candidate = atPostingHour(
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset)
    );
    // Today only counts if the posting hour has not already passed — otherwise
    // the picker opens on a time that is already invalid.
    if (candidate.getTime() <= now.getTime()) continue;
    if (taken.has(dayStart(candidate.getTime()))) continue;
    return candidate;
  }
  return null;
}

/** Why a chosen time cannot be used, in plain words. `null` means it can. */
export function slotRefusal(
  when: Date | null,
  scheduled: ScheduledPost[],
  opts: { now?: Date; ignoreSlotId?: string } = {}
): string | null {
  const now = opts.now ?? new Date();
  if (!when || !Number.isFinite(when.getTime())) return "Pick a date and time.";
  if (when.getTime() <= now.getTime()) return "That time has already passed.";

  const clash = scheduled.find((slot) => {
    if (opts.ignoreSlotId && slot.reviewId === opts.ignoreSlotId) return false;
    const parsed = Date.parse(slot.scheduledFor);
    return Number.isFinite(parsed) && dayStart(parsed) === dayStart(when.getTime());
  });
  if (clash) return `Something is already going out that day — ${clash.subject}.`;

  return null;
}

/** For an <input type="datetime-local">, which wants local time with no zone. */
export function toDateTimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/**
 * Parse what the input gives back.
 *
 * `datetime-local` has NO timezone, so `new Date(value)` is parsed as LOCAL by
 * the browser — which is what we want, and is why the value is built up from
 * parts here rather than handed to `Date.parse`, whose handling of a
 * zone-less string has historically differed between engines.
 */
export function fromDateTimeLocal(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi] = match;
  const parsed = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    0,
    0
  );
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}
